import React, { useState, useEffect } from 'react';
import { Contact } from '../types/Contact';
import { User } from '@supabase/supabase-js';
import { Bot, Loader2, Copy, Check, MessageSquare, ChevronDown, ChevronUp, Edit3, Trash2, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { updateContactCore, createSavedMessageCore, updateSavedMessageCore, deleteSavedMessageCore, listSavedMessagesForCollectionCore } from '@gv/core';
import ConfirmationModal from './ConfirmationModal';

// Helper function to convert URLs to clickable links with better detection
const convertUrlsToLinks = (text: string) => {
    // More comprehensive URL regex that handles various URL formats
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}(?:\/[^^\s<>"']*)?)/gi;

    return text.replace(urlRegex, (url) => {
        // Add protocol if missing
        const href = url.startsWith('http') ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #60a5fa; text-decoration: underline; cursor: pointer;">${url}</a>`;
    });
};

// Helper function to copy text with clickable links and better fallbacks
const copyWithClickableLinks = async (text: string): Promise<boolean> => {
    try {
        // Check if ClipboardItem is supported (for rich HTML copy)
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
            // Convert URLs to clickable links for HTML
            const htmlContent = `
                <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
                    ${convertUrlsToLinks(text).replace(/\n/g, '<br>')}
                </div>
            `;

            // Create a ClipboardItem with both HTML and plain text
            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([htmlContent], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardItem]);
            return true;
        } else {
            // Fallback for browsers that don't support ClipboardItem
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (error) {
        console.warn('Advanced clipboard failed, trying basic copy:', error);
        // Ultimate fallback to plain text copy
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (fallbackError) {
            console.error('All clipboard methods failed:', fallbackError);
            // Try the old school way for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                return true;
            } catch {
                return false;
            }
        }
    }
};

interface SavedMessage {
    id: string;
    message_content: string;
    tone: 'cold' | 'warm' | 'hot';
    created_at: string;
    updated_at: string;
}

interface PersonalizedMessageGeneratorProps {
    contact: Contact;
    user: User;
    personalProfile?: string;
    collectionContext?: string; // Collection-specific message context
    collectionName?: string; // Name of the collection for UI display
    collectionId?: string; // Collection ID for saving messages
    onNotification: (message: string, type?: 'success' | 'error') => void;
    onSave: () => void;
}

export default function PersonalizedMessageGenerator({
    contact,
    user,
    personalProfile,
    collectionContext,
    collectionName,
    collectionId,
    onNotification,
    onSave
}: PersonalizedMessageGeneratorProps) {
    const [generatedMessage, setGeneratedMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [relationshipTemp, setRelationshipTemp] = useState<'cold' | 'warm' | 'hot'>('warm');
    const [isViewingExisting, setIsViewingExisting] = useState(false);
    const [showSavedMessages, setShowSavedMessages] = useState(false);
    const [savedMessages, setSavedMessages] = useState<SavedMessage[]>([]);
    const [editingMessage, setEditingMessage] = useState<SavedMessage | null>(null);
    const [editedMessageContent, setEditedMessageContent] = useState('');

    // Helper function to check if contact has existing AI message
    const hasExistingMessage = (contact: Contact) => {
        const hasAIField = contact.ai_generated_message && contact.ai_generated_message.trim() !== '';
        const hasAIInNotes = contact.notes && contact.notes.includes('--- AI Generated Message');
        return hasAIField || hasAIInNotes;
    };

    // Helper function to get all existing AI messages
    const getAllAIMessages = (contact: Contact) => {
        const messages: Array<{ id: string, date: string, tone: string, content: string }> = [];

        if (contact.notes && contact.notes.includes('--- AI Generated Message')) {
            const notes = contact.notes;
            const messagePattern = /--- AI Generated Message \(([^)]+)\) ---\nTone: ([^\n]+)\n([\s\S]*?)(?=--- End AI Message ---|$)/g;
            let match;

            while ((match = messagePattern.exec(notes)) !== null) {
                messages.push({
                    id: Date.now() + Math.random().toString(),
                    date: match[1],
                    tone: match[2].trim(),
                    content: match[3].trim()
                });
            }
        }

        return messages.reverse(); // Show newest first
    };

    // Helper function to get existing message
    const getExistingMessage = (contact: Contact) => {
        if (contact.ai_generated_message && contact.ai_generated_message.trim() !== '') {
            return contact.ai_generated_message;
        }
        if (contact.notes && contact.notes.includes('--- AI Generated Message')) {
            const notes = contact.notes;
            const aiMessageStart = notes.lastIndexOf('--- AI Generated Message');
            if (aiMessageStart !== -1) {
                const messageSection = notes.substring(aiMessageStart);
                const endMarker = messageSection.indexOf('--- End AI Message ---');
                if (endMarker !== -1) {
                    const fullMessage = messageSection.substring(0, endMarker);
                    const lines = fullMessage.split('\n');
                    const messageStart = lines.findIndex(line => line.startsWith('Tone:')) + 1;
                    if (messageStart > 0 && messageStart < lines.length) {
                        return lines.slice(messageStart).join('\n').trim();
                    }
                }
            }
        }
        return '';
    };

    // Clear generated message when relationship temperature changes
    useEffect(() => {
        setGeneratedMessage('');
        setIsViewingExisting(false);
    }, [relationshipTemp]);

    // Clear states when contact changes
    useEffect(() => {
        setGeneratedMessage('');
        setIsViewingExisting(false);
        setCopied(false);
        if (collectionId) {
            fetchSavedMessages();
        }
    }, [contact, collectionId]);

    // Fetch saved messages for the collection
    const fetchSavedMessages = async () => {
        if (!collectionId) return;

        try {
            const data = await listSavedMessagesForCollectionCore(supabase as any, collectionId, user.id);
            setSavedMessages(data || []);
        } catch (error) {
            console.error('Error fetching saved messages:', error);
        }
    };

    // Save message to collection
    const saveMessageToCollection = async (messageContent: string, tone: 'cold' | 'warm' | 'hot') => {
        if (!collectionId) return;

        try {
            await createSavedMessageCore(supabase as any, { collectionId, userId: user.id, messageContent, tone });
            await fetchSavedMessages(); // Refresh the list
            onNotification('Message saved to collection!', 'success');
        } catch (error) {
            console.error('Error saving message:', error);
            onNotification('Failed to save message', 'error');
        }
    };

    // Update existing message
    const updateSavedMessage = async (messageId: string, messageContent: string) => {
        try {
            await updateSavedMessageCore(supabase as any, messageId, { messageContent });
            await fetchSavedMessages(); // Refresh the list
            setEditingMessage(null);
            onNotification('Message updated!', 'success');
        } catch (error) {
            console.error('Error updating message:', error);
            onNotification('Failed to update message', 'error');
        }
    };

    // Delete message
    const deleteSavedMessage = async (messageId: string) => {
        // Using a simple confirm for now - can be replaced with modal later
        if (!window.confirm('Are you sure you want to delete this message?')) return;

        try {
            await deleteSavedMessageCore(supabase as any, messageId);
            await fetchSavedMessages(); // Refresh the list
            onNotification('Message deleted!', 'success');
        } catch (error) {
            console.error('Error deleting message:', error);
            onNotification('Failed to delete message', 'error');
        }
    };

    const generatePrompt = async (contactData: any, temperature: 'cold' | 'warm' | 'hot') => {
        try {
            // Import the prompt file from src/ai-prompts
            const promptModule = await import(`../ai-prompts/${temperature}.txt?raw`);
            const promptTemplate = promptModule.default;

            // Message approach context: Use EITHER personal profile OR collection context (never both)
            // If in a collection, use collection context only
            // If not in a collection, use personal profile only
            const messageApproachContext = collectionContext || personalProfile || 'General professional outreach';

            const prompt = promptTemplate
                .replace('{personalProfile}', messageApproachContext)
                .replace('{contactData}', JSON.stringify(contactData, null, 2))
                .replace('{messageContext}', messageApproachContext);

            return prompt;
        } catch (error) {
            console.error(`Error loading prompt for ${temperature}:`, error);
            // Use collection context if available, otherwise use global personal profile
            // When in collection context, ONLY use collection context, never fall back to personal profile
            // When NOT in collection context, use personal profile
            const contextToUse = collectionId && collectionContext ? collectionContext :
                collectionId ? '' : // If in collection but no context, use empty string
                    personalProfile || '';

            return `You are helping to craft a personalized ${temperature} message.

My Personal Profile:
${contextToUse}

Contact Information:
${JSON.stringify(contactData, null, 2)}

Please generate a ${temperature} message for this contact.`;
        }
    };

    const generatePersonalizedMessage = async () => {
        // Validation logic based on context:
        // - If in collection: MUST have collection context, personal profile is ignored
        // - If NOT in collection: MUST have personal profile
        if (collectionId) {
            // In collection context - ONLY check for collection context
            if (!collectionContext || !collectionContext.trim()) {
                onNotification(`Please set up a message context for the "${collectionName}" collection in Settings.`, 'error');
                return;
            }
        } else {
            // NOT in collection context - check for personal profile
            if (!personalProfile || !personalProfile.trim()) {
                onNotification('Please set up your personal profile in Settings first.', 'error');
                return;
            }
        }

        setIsGenerating(true);
        setGeneratedMessage('');

        try {
            const contactData = {
                name: `${contact.first_name} ${contact.last_name}`,
                title: contact.title,
                company: contact.company,
                email: contact.email,
                phone: contact.phone,
                notes: contact.notes,
                personal_details: contact.personal_details,
                tier: contact.tier,
                industry: contact.industry,
                location: contact.location,
                linkedin: contact.linkedin_profile_url,
                structured_notes: contact.structured_notes,
                // Investment Intelligence Fields
                personal_investment_score: contact.personal_investment_score,
                personal_investment_reasoning: contact.personal_investment_reasoning,
                referral_potential_score: contact.referral_potential_score,
                referral_potential_reasoning: contact.referral_potential_reasoning,
                recommended_approach: contact.recommended_approach,
                key_factors: contact.key_factors,
                accredited_investor_likelihood: contact.accredited_investor_likelihood,
                estimated_investment_capacity: contact.estimated_investment_capacity,
                industry_relevance: contact.industry_relevance,
                wealth_indicators: contact.wealth_indicators,
                financial_sophistication: contact.financial_sophistication,
                summary: contact.summary,
                custom_fields: {
                    business_email: contact.business_email,
                    business_phone: contact.business_phone,
                    mobile: contact.mobile
                }
            };

            const response = await supabase.functions.invoke('gemini-ai', {
                body: {
                    prompt: await generatePrompt(contactData, relationshipTemp)
                }
            });

            if (response.error) {
                throw new Error(response.error.message || 'Failed to generate message');
            }

            if (response.data && response.data.response) {
                const aiMessage = response.data.response;
                setGeneratedMessage(aiMessage);

                // Save to collection if we have a collection ID
                if (collectionId) {
                    await saveMessageToCollection(aiMessage, relationshipTemp);
                }

                // Save the AI-generated message to the contact's notes
                try {
                    const currentNotes = contact.notes || '';
                    const aiMessageSection = `\n\n--- AI Generated Message (${new Date().toLocaleDateString()}) ---\nTone: ${relationshipTemp.toUpperCase()}\n${aiMessage}\n--- End AI Message ---`;
                    const updatedNotes = currentNotes + aiMessageSection;

                    try {
                        await updateContactCore(supabase as any, String(contact.id), {
                            notes: updatedNotes,
                            ai_last_updated: new Date().toISOString() as any,
                        } as any);
                        onNotification('Message generated and saved successfully!', 'success');
                    } catch (updateError) {
                        console.error('Error saving AI message to contact:', updateError);
                        onNotification('Message generated and saved successfully!', 'success');
                    }
                } catch (saveError) {
                    console.error('Error saving AI message:', saveError);
                }
            } else {
                throw new Error('No message generated');
            }
        } catch (error) {
            console.error('Error generating message:', error);
            let errorMessage = 'Failed to generate message. ';

            if (error instanceof Error) {
                if (error.message.includes('CORS')) {
                    errorMessage += 'Network configuration issue. Please try again later.';
                } else if (error.message.includes('Failed to send a request to the Edge Function')) {
                    errorMessage += 'AI service is temporarily unavailable. Please try again later.';
                } else {
                    errorMessage += error.message;
                }
            } else {
                errorMessage += 'Please try again.';
            }

            onNotification(errorMessage, 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            const success = await copyWithClickableLinks(generatedMessage);
            if (success) {
                setCopied(true);
                onNotification('Message copied with clickable links!', 'success');
                setTimeout(() => setCopied(false), 2000);
            } else {
                throw new Error('Copy failed');
            }
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            onNotification('Failed to copy to clipboard', 'error');
        }
    };

    const viewExistingMessage = () => {
        const existingMessage = getExistingMessage(contact);
        setGeneratedMessage(existingMessage);
        setIsViewingExisting(true);
    };

    // Show error message based on context
    if (collectionId) {
        // In collection context - only check for collection context
        if (!collectionContext || !collectionContext.trim()) {
            return (
                <div className="bg-gray-900 border border-gray-700 p-4">
                    <p className="text-gray-300 text-sm">
                        <strong>Setup Required:</strong> Please configure the message context for this collection in Collection Settings to enable AI message generation.
                    </p>
                </div>
            );
        }
    } else {
        // NOT in collection context - check for personal profile
        if (!personalProfile || !personalProfile.trim()) {
            return (
                <div className="bg-gray-900 border border-gray-700 p-4">
                    <p className="text-gray-300 text-sm">
                        <strong>Setup Required:</strong> Please configure your personal profile in Settings to enable AI message generation.
                    </p>
                </div>
            );
        }
    }

    return (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6 space-y-6 rounded-lg">
            {/* Header */}
            <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-900 border border-gray-700 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-light text-white tracking-wide">AI MESSAGE</h3>
            </div>

            {/* Choose Your Tone Section */}
            <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Choose Your Tone</h4>
                <div className="space-y-2">
                    <button
                        onClick={() => setRelationshipTemp('cold')}
                        className={`w-full px-4 py-3 rounded-lg border transition-all font-medium ${relationshipTemp === 'cold'
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                            }`}
                    >
                        ❄️ Cold
                    </button>
                    <button
                        onClick={() => setRelationshipTemp('warm')}
                        className={`w-full px-4 py-3 rounded-lg border transition-all font-medium ${relationshipTemp === 'warm'
                            ? 'bg-orange-600 border-orange-500 text-white'
                            : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                            }`}
                    >
                        🤝 Warm
                    </button>
                    <button
                        onClick={() => setRelationshipTemp('hot')}
                        className={`w-full px-4 py-3 rounded-lg border transition-all font-medium ${relationshipTemp === 'hot'
                            ? 'bg-red-600 border-red-500 text-white'
                            : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                            }`}
                    >
                        🔥 Hot
                    </button>
                </div>
                <div className="text-xs text-gray-400">
                    {relationshipTemp === 'cold' && "Professional introduction for new contacts"}
                    {relationshipTemp === 'warm' && "Friendly reconnection for existing connections"}
                    {relationshipTemp === 'hot' && "Casual and direct for regular contacts"}
                </div>
            </div>

            {/* Generate Button */}
            <button
                onClick={() => {
                    setIsViewingExisting(false);
                    generatePersonalizedMessage();
                }}
                disabled={isGenerating}
                className="w-full bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white py-3 px-4 text-sm font-medium transition-colors border border-gray-700 hover:border-gray-600 flex items-center justify-center rounded"
            >
                {isGenerating ? (
                    <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                    </>
                ) : (
                    <>
                        <Bot className="w-4 h-4 mr-2" />
                        Generate New Message
                    </>
                )}
            </button>            {/* Generated Message Display */}
            {generatedMessage && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h5 className="text-sm font-medium text-white">
                            {isViewingExisting ? 'Existing Message' : 'Generated Message'}
                        </h5>
                        {isViewingExisting && (
                            <span className="text-xs text-green-300 bg-green-800/30 px-2 py-1 border border-green-700/50">
                                Previously Generated
                            </span>
                        )}
                    </div>

                    <div className="bg-gray-900 border border-gray-700 p-4 max-h-60 overflow-y-auto">
                        <div
                            className="text-gray-200 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{
                                __html: convertUrlsToLinks(generatedMessage)
                            }}
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={copyToClipboard}
                            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 text-sm font-medium transition-colors border border-gray-700 hover:border-gray-600 flex items-center"
                        >
                            {copied ? (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4 mr-2" />
                                    Copy Message
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Saved Messages Section - Collapsible for Collection */}
            {(collectionId && savedMessages.length > 0) && (
                <div className="border-t border-gray-700 pt-4">
                    <button
                        onClick={() => setShowSavedMessages(!showSavedMessages)}
                        className="w-full flex items-center justify-center space-x-2 text-gray-300 hover:text-white transition-colors py-2 group"
                    >
                        <MessageSquare className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium">
                            Collection Messages ({savedMessages.length})
                        </span>
                        {showSavedMessages ? (
                            <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                        )}
                    </button>

                    {showSavedMessages && (
                        <div className="mt-4 space-y-3 max-h-48 overflow-y-auto border border-gray-700 rounded bg-gray-800 p-3">
                            {savedMessages.map((message) => (
                                <div
                                    key={message.id}
                                    className="bg-gray-900 border border-gray-700 p-3 cursor-pointer hover:bg-gray-800 transition-colors"
                                    onClick={() => {
                                        setEditingMessage(message);
                                        setEditedMessageContent(message.message_content);
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-gray-400">
                                            {new Date(message.created_at).toLocaleDateString()} • {message.tone.toUpperCase()}
                                        </span>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    copyWithClickableLinks(message.message_content).then(success => {
                                                        if (success) {
                                                            onNotification('Message copied with clickable links!', 'success');
                                                        } else {
                                                            onNotification('Failed to copy message', 'error');
                                                        }
                                                    });
                                                }}
                                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-blue-900/20"
                                            >
                                                Copy
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteSavedMessage(message.id);
                                                }}
                                                className="text-xs text-red-400 hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-red-900/20"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className="text-gray-200 text-sm"
                                        dangerouslySetInnerHTML={{
                                            __html: convertUrlsToLinks(message.message_content)
                                        }}
                                    />
                                    <div className="text-xs text-gray-500 mt-2">
                                        Click to edit this message
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Fallback to Contact Saved Messages if no collection */}
            {(!collectionId && getAllAIMessages(contact).length > 0) && (
                <div className="border-t border-gray-700 pt-4">
                    <button
                        onClick={() => setShowSavedMessages(!showSavedMessages)}
                        className="w-full flex items-center justify-center space-x-2 text-gray-300 hover:text-white transition-colors py-2 group"
                    >
                        <MessageSquare className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium">
                            Saved Messages ({getAllAIMessages(contact).length})
                        </span>
                        {showSavedMessages ? (
                            <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                        )}
                    </button>

                    {showSavedMessages && (
                        <div className="mt-4 space-y-3 max-h-48 overflow-y-auto border border-gray-700 rounded bg-gray-800 p-3">
                            {getAllAIMessages(contact).map((message) => (
                                <div key={message.id} className="bg-gray-900 border border-gray-700 p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-gray-400">
                                            {message.date} • {message.tone}
                                        </span>
                                        <button
                                            onClick={() => {
                                                copyWithClickableLinks(message.content).then(success => {
                                                    if (success) {
                                                        onNotification('Message copied with clickable links!', 'success');
                                                    } else {
                                                        onNotification('Failed to copy message', 'error');
                                                    }
                                                });
                                            }}
                                            className="text-xs text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-blue-900/20"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <div
                                        className="text-gray-200 text-sm"
                                        dangerouslySetInnerHTML={{
                                            __html: convertUrlsToLinks(message.content)
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Collection Context Indicator at bottom */}
            {collectionContext && collectionName && (
                <div className="mt-4 flex justify-center">
                    <span className="text-xs bg-purple-600/20 text-purple-300 px-3 py-1.5 rounded-full border border-purple-500/30 flex items-center">
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-2"></span>
                        Using {collectionName} context
                    </span>
                </div>
            )}

            {/* Edit Message Modal */}
            {editingMessage && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-600">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-white">Edit Message</h3>
                                <button
                                    onClick={() => setEditingMessage(null)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <p className="text-gray-400 text-sm mt-1">
                                {editingMessage.tone.toUpperCase()} tone • Created {new Date(editingMessage.created_at).toLocaleDateString()}
                            </p>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            <textarea
                                value={editedMessageContent}
                                onChange={(e) => setEditedMessageContent(e.target.value)}
                                className="w-full h-48 p-4 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-gray-600 resize-none"
                                placeholder="Edit your message..."
                            />
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-gray-700 flex justify-between items-center">
                            <button
                                onClick={() => {
                                    copyWithClickableLinks(editedMessageContent).then(success => {
                                        if (success) {
                                            onNotification('Message copied with clickable links!', 'success');
                                        } else {
                                            onNotification('Failed to copy message', 'error');
                                        }
                                    });
                                }}
                                className="flex items-center px-4 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                                <Copy className="w-4 h-4 mr-2" />
                                Copy
                            </button>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setEditingMessage(null)}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => updateSavedMessage(editingMessage.id, editedMessageContent)}
                                    disabled={!editedMessageContent.trim()}
                                    className="flex items-center px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-700 hover:border-gray-600"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
